/* ==========================================================================
   INDIGO OSLO — motion.js
   Lenis + GSAP orchestration.

   Every effect is registered through `fx()`, which is a no-op when motion is
   disabled, so the whole layer collapses cleanly for reduced-motion users and
   the page below it stays fully functional.
   ========================================================================== */

(function () {
  'use strict';

  var T = window.theme || {};

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(hover: hover) and (min-width: 1000px)').matches;
  var MOTION = T.motion !== false && !REDUCED;

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var SplitText = window.SplitText;
  var Flip = window.Flip;

  /* ----------------------------------------------------------------------
     Reveal everything immediately if the motion stack is unavailable.
     A missing library must never leave the page blank.
     ---------------------------------------------------------------------- */

  function revealAll() {
    document.querySelectorAll('[data-reveal], [data-split]').forEach(function (el) {
      el.classList.add('is-revealed');
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    document.querySelectorAll('[data-img-reveal] > *').forEach(function (el) {
      el.style.clipPath = 'none';
    });
    var curtain = document.getElementById('curtain');
    if (curtain) curtain.classList.add('is-gone');
  }

  if (!gsap || !MOTION) {
    revealAll();
  }
  if (gsap && ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    if (SplitText) gsap.registerPlugin(SplitText);
    if (Flip) gsap.registerPlugin(Flip);
  }

  function fx(fn) {
    if (!MOTION || !gsap) return;
    try {
      fn();
    } catch (err) {
      /* One broken effect must not take the rest of the page with it. */
      if (window.console) console.warn('[motion]', err);
    }
  }

  /* ======================================================================
     1. Lenis smooth scroll — drives every ScrollTrigger
     ====================================================================== */

  var lenis = null;

  fx(function () {
    if (!window.Lenis) return;

    lenis = new window.Lenis({
      duration: 1.1,
      easing: function (t) {
        return Math.min(1, 1.001 - Math.pow(2, -10 * t));
      },
      smoothWheel: true,
      syncTouch: false, // native momentum on touch feels better than emulated
      touchMultiplier: 1.6
    });

    window.lenisInstance = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add(function (time) {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Anchor links route through Lenis so they inherit the same easing
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id === '#' || id === '#main') return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -100 });
    });
  });

  /* ======================================================================
     2. Curtain intro — first visit of the session only
     ====================================================================== */

  var curtain = document.getElementById('curtain');

  function openCurtain() {
    if (!curtain) return;

    if (!MOTION || !gsap) {
      curtain.classList.add('is-gone');
      return;
    }

    var seen = false;
    try {
      seen = sessionStorage.getItem('indigo:seen') === '1';
    } catch (e) {
      /* private mode — just play it */
    }

    if (seen) {
      curtain.classList.add('is-gone');
      startPage();
      return;
    }

    try {
      sessionStorage.setItem('indigo:seen', '1');
    } catch (e) {}

    if (lenis) lenis.stop();

    var mark = curtain.querySelector('.curtain__mark span');

    var tl = gsap.timeline({
      onComplete: function () {
        curtain.classList.add('is-gone');
        if (lenis) lenis.start();
        startPage();
      }
    });

    tl.from(mark, { yPercent: 115, duration: 1, ease: 'expo.out' })
      .to(mark, { yPercent: -115, duration: 0.7, ease: 'expo.in' }, '+=0.25')
      .to(curtain, { yPercent: -100, duration: 0.9, ease: 'expo.inOut' }, '-=0.35');
  }

  /* ======================================================================
     3. SplitText — per-character rise on display headings
     ====================================================================== */

  function splitHeadings() {
    if (!SplitText) return;

    document.querySelectorAll('[data-split]').forEach(function (el) {
      // Fonts must be loaded before splitting or line breaks land wrong.
      var split = new SplitText(el, {
        type: 'lines,chars',
        linesClass: 'split-line'
      });

      // Each line clips its own characters
      split.lines.forEach(function (line) {
        line.style.overflow = 'hidden';
        line.style.paddingBottom = '0.08em';
        line.style.marginBottom = '-0.08em';
      });

      gsap.set(el, { opacity: 1 });

      gsap.from(split.chars, {
        yPercent: 118,
        duration: 1,
        ease: 'expo.out',
        stagger: 0.012,
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true
        }
      });
    });
  }

  /* ======================================================================
     4. Card + block reveals
     ====================================================================== */

  function reveals() {
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: 'expo.out',
        delay: (Number(el.dataset.delay) || 0) / 1000,
        scrollTrigger: { trigger: el, start: 'top 90%', once: true }
      });
    });

    // Image plates wipe open, image counter-scales from 1.15 → 1
    document.querySelectorAll('[data-img-reveal]').forEach(function (frame) {
      var inner = frame.children;
      if (!inner.length) return;

      gsap
        .timeline({ scrollTrigger: { trigger: frame, start: 'top 92%', once: true } })
        .fromTo(
          inner,
          { clipPath: 'inset(0 0 100% 0)' },
          { clipPath: 'inset(0 0 0% 0)', duration: 1.15, ease: 'expo.out' }
        )
        .fromTo(
          frame.querySelectorAll('img'),
          { scale: 1.15 },
          { scale: 1, duration: 1.4, ease: 'expo.out' },
          0
        );
    });
  }

  /* ======================================================================
     5. Hero — scrubbed parallax, title counter-drift
     ====================================================================== */

  function hero() {
    var section = document.querySelector('[data-hero]');
    if (!section) return;

    var img = section.querySelector('[data-hero-img]');
    var copy = section.querySelector('[data-hero-copy]');

    if (img) {
      gsap.to(img, {
        yPercent: 14,
        ease: 'none',
        scrollTrigger: { trigger: section, start: 'top top', end: 'bottom top', scrub: 0.6 }
      });
    }

    if (copy) {
      gsap.to(copy, {
        yPercent: -18,
        opacity: 0.25,
        ease: 'none',
        scrollTrigger: { trigger: section, start: 'top top', end: 'bottom top', scrub: 0.6 }
      });
    }
  }

  /* ======================================================================
     6. Pinned horizontal lookbook
     ====================================================================== */

  function lookbook() {
    document.querySelectorAll('[data-lookbook]').forEach(function (section) {
      var viewport = section.querySelector('[data-lookbook-viewport]');
      var track = section.querySelector('[data-lookbook-track]');
      if (!track) return;

      // Below 700px the rail is a native swipe instead — pinning a narrow
      // viewport fights the browser's own scroll.
      var mm = gsap.matchMedia();

      mm.add('(min-width: 700px)', function () {
        var distance = function () {
          return Math.max(0, track.scrollWidth - window.innerWidth);
        };

        var tween = gsap.to(track, {
          x: function () {
            return -distance();
          },
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: function () {
              return '+=' + distance();
            },
            pin: true,
            scrub: 0.8,
            anticipatePin: 1,
            invalidateOnRefresh: true
          }
        });

        // Items drift slightly against the track for depth
        gsap.utils.toArray(track.querySelectorAll('[data-lookbook-item] img')).forEach(function (im) {
          gsap.fromTo(
            im,
            { xPercent: -6 },
            {
              xPercent: 6,
              ease: 'none',
              scrollTrigger: {
                trigger: im.closest('[data-lookbook-item]'),
                containerAnimation: tween,
                start: 'left right',
                end: 'right left',
                scrub: true
              }
            }
          );
        });

        return function () {
          tween.scrollTrigger && tween.scrollTrigger.kill();
          tween.kill();
          gsap.set(track, { clearProps: 'x' });
        };
      });

      mm.add('(max-width: 699px)', function () {
        if (viewport) viewport.classList.add('is-native');
        return function () {
          if (viewport) viewport.classList.remove('is-native');
        };
      });
    });
  }

  /* ======================================================================
     7. Marquees — seamless, direction-aware
     ====================================================================== */

  function marquees() {
    document.querySelectorAll('[data-marquee]').forEach(function (el) {
      var track = el.querySelector('[data-marquee-track]');
      if (!track) return;

      var group = track.querySelector('.marquee__group');
      if (!group) return;

      var speed = Number(el.dataset.speed) || 45;

      var tween = gsap.to(track, {
        x: function () {
          return -group.offsetWidth;
        },
        duration: speed,
        ease: 'none',
        repeat: -1,
        modifiers: {
          x: function (x) {
            var w = group.offsetWidth;
            return (parseFloat(x) % w) + 'px';
          }
        }
      });

      // Scroll direction flips the marquee and briefly speeds it up
      ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: function (self) {
          var dir = self.direction;
          gsap.to(tween, { timeScale: dir * 1, duration: 0.4, overwrite: true });
        }
      });
    });
  }

  /* ======================================================================
     8. Magnetic cursor
     ====================================================================== */

  function cursor() {
    if (!DESKTOP) return;

    var el = document.getElementById('cursor');
    var label = document.getElementById('cursor-label');
    if (!el) return;

    var x = gsap.quickTo(el, 'x', { duration: 0.35, ease: 'power3' });
    var y = gsap.quickTo(el, 'y', { duration: 0.35, ease: 'power3' });

    window.addEventListener(
      'mousemove',
      function (e) {
        el.classList.add('is-active');
        x(e.clientX);
        y(e.clientY);
      },
      { passive: true }
    );

    document.addEventListener('mouseleave', function () {
      el.classList.remove('is-active');
    });

    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest('[data-product-card]');
      var link = e.target.closest('a, button');

      if (card) {
        el.classList.add('is-view');
        el.classList.remove('is-link');
        if (label) label.textContent = T.strings.view || 'Se';
      } else if (link) {
        el.classList.add('is-link');
        el.classList.remove('is-view');
        if (label) label.textContent = '';
      } else {
        el.classList.remove('is-view', 'is-link');
        if (label) label.textContent = '';
      }
    });

    // Magnetic pull on flagged elements
    document.querySelectorAll('[data-magnetic]').forEach(function (node) {
      var mx = gsap.quickTo(node, 'x', { duration: 0.5, ease: 'power3' });
      var my = gsap.quickTo(node, 'y', { duration: 0.5, ease: 'power3' });

      node.addEventListener('mousemove', function (e) {
        var r = node.getBoundingClientRect();
        mx((e.clientX - (r.left + r.width / 2)) * 0.32);
        my((e.clientY - (r.top + r.height / 2)) * 0.32);
      });

      node.addEventListener('mouseleave', function () {
        mx(0);
        my(0);
      });
    });
  }

  /* ======================================================================
     9. Grid → product Flip transition
     Uses the View Transition API where available, and hands the browser a
     named element so the card image morphs into the PDP hero.
     ====================================================================== */

  function flipToProduct() {
    if (!('startViewTransition' in document)) return;

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-card-link], [data-card-title-link]');
      if (!link) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

      var card = link.closest('[data-product-card]');
      if (!card) return;

      var img = card.querySelector('.card__img:not(.card__img--alt)');
      if (img) img.style.viewTransitionName = 'product-img';

      // The name must be released or the next navigation inherits it
      setTimeout(function () {
        if (img) img.style.viewTransitionName = '';
      }, 900);
    });
  }

  /* ======================================================================
     10. Header — hide on scroll down, solidify past the hero
     ====================================================================== */

  function header() {
    var el = document.getElementById('header');
    if (!el) return;

    var last = 0;
    var threshold = 80;

    function update() {
      var y = window.scrollY;
      el.classList.toggle('header--solid', y > threshold);

      var goingDown = y > last && y > 300;
      var drawerOpen = window.theme.ui && window.theme.ui.isOpen();
      el.classList.toggle('header--hidden', goingDown && !drawerOpen);

      last = y;
    }

    if (lenis) {
      lenis.on('scroll', update);
    } else {
      window.addEventListener('scroll', update, { passive: true });
    }
    update();
  }

  /* ======================================================================
     11. Scroll progress rail
     ====================================================================== */

  function rail() {
    var fill = document.getElementById('rail-fill');
    if (!fill || !gsap) return;

    gsap.to(fill, {
      scaleY: 1,
      ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 }
    });
  }

  /* ======================================================================
     12. Mega menu
     ====================================================================== */

  function megaMenu() {
    var triggers = document.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length) return;

    var openPanel = null;
    var closeTimer = null;

    function close(panel) {
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.hidden = true;
      var trigger = document.querySelector('[data-mega-trigger="' + panel.id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (openPanel === panel) openPanel = null;
    }

    function open(panel, trigger) {
      clearTimeout(closeTimer);
      if (openPanel && openPanel !== panel) close(openPanel);
      panel.hidden = false;
      // Force a frame so the transition runs from the hidden state
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      trigger.setAttribute('aria-expanded', 'true');
      openPanel = panel;

      if (MOTION && gsap) {
        gsap.from(panel.querySelectorAll('.mega__link, .plate'), {
          opacity: 0,
          y: 14,
          duration: 0.5,
          stagger: 0.025,
          ease: 'power3.out',
          clearProps: 'all'
        });
      }
    }

    triggers.forEach(function (trigger) {
      var panel = document.getElementById(trigger.dataset.megaTrigger);
      if (!panel) return;

      trigger.addEventListener('mouseenter', function () {
        open(panel, trigger);
      });
      trigger.addEventListener('focus', function () {
        open(panel, trigger);
      });
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openPanel === panel ? close(panel) : open(panel, trigger);
      });

      [trigger, panel].forEach(function (node) {
        node.addEventListener('mouseleave', function () {
          closeTimer = setTimeout(function () {
            close(panel);
          }, 180);
        });
        node.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
        });
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openPanel) close(openPanel);
    });

    document.addEventListener('click', function (e) {
      if (openPanel && !e.target.closest('[data-mega], [data-mega-trigger]')) close(openPanel);
    });
  }

  /* ======================================================================
     13. Brand wall — cursor-following preview
     ====================================================================== */

  function brandPeek() {
    var wall = document.querySelector('[data-brand-wall]');
    if (!wall || !DESKTOP || !gsap) return;

    var peek = document.createElement('div');
    peek.className = 'brand-peek';
    peek.setAttribute('aria-hidden', 'true');
    peek.innerHTML = '<img alt="">';
    document.body.appendChild(peek);

    var img = peek.querySelector('img');
    var px = gsap.quickTo(peek, 'x', { duration: 0.6, ease: 'power3' });
    var py = gsap.quickTo(peek, 'y', { duration: 0.6, ease: 'power3' });

    wall.querySelectorAll('[data-brand-row]').forEach(function (row) {
      var src = row.dataset.peek;
      if (!src) return;

      row.addEventListener('mouseenter', function () {
        img.src = src;
        gsap.to(peek, { opacity: 1, duration: 0.35, ease: 'power2.out' });
      });

      row.addEventListener('mouseleave', function () {
        gsap.to(peek, { opacity: 0, duration: 0.3 });
      });
    });

    wall.addEventListener(
      'mousemove',
      function (e) {
        px(e.clientX - 100);
        py(e.clientY - 150);
      },
      { passive: true }
    );
  }

  /* ======================================================================
     14. Accordions — height-animated via grid-template-rows
     ====================================================================== */

  function accordions() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.acc__btn');
      if (!btn) return;

      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
      panel.classList.toggle('is-open', !isOpen);

      if (ScrollTrigger) {
        setTimeout(function () {
          ScrollTrigger.refresh();
        }, 500);
      }
    });
  }

  /* ======================================================================
     15. Product media — click to zoom
     ====================================================================== */

  function lightbox() {
    var box = document.querySelector('[data-lightbox]');
    if (!box) return;

    var target = box.querySelector('img');

    document.addEventListener('click', function (e) {
      var plate = e.target.closest('[data-zoom]');
      if (plate) {
        var src = plate.dataset.zoom;
        if (!src) return;
        target.src = src;
        box.classList.add('is-open');
        box.setAttribute('aria-hidden', 'false');
        if (lenis) lenis.stop();
        document.documentElement.style.overflow = 'hidden';
        return;
      }

      if (box.classList.contains('is-open')) {
        box.classList.remove('is-open');
        box.setAttribute('aria-hidden', 'true');
        if (lenis) lenis.start();
        document.documentElement.style.overflow = '';
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && box.classList.contains('is-open')) {
        box.classList.remove('is-open');
        box.setAttribute('aria-hidden', 'true');
        if (lenis) lenis.start();
        document.documentElement.style.overflow = '';
      }
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  function startPage() {
    fx(splitHeadings);
    fx(reveals);
    fx(hero);
    fx(lookbook);
    fx(marquees);
    fx(rail);
    fx(cursor);
    fx(brandPeek);
    fx(flipToProduct);

    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  function boot() {
    // These run regardless of the motion setting — they are behaviour, not decoration.
    header();
    megaMenu();
    accordions();
    lightbox();

    if (!MOTION || !gsap) {
      revealAll();
      return;
    }

    // Wait for fonts before SplitText measures anything.
    var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    ready
      .then(function () {
        openCurtain();
      })
      .catch(function () {
        openCurtain();
      });

    // Safety net: never leave the curtain up if something stalls.
    setTimeout(function () {
      if (curtain && !curtain.classList.contains('is-gone')) {
        curtain.classList.add('is-gone');
        if (lenis) lenis.start();
        startPage();
      }
    }, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Drawer open/close must pause ScrollTrigger-driven pins cleanly
  document.addEventListener('ui:close', function () {
    if (ScrollTrigger) ScrollTrigger.refresh();
  });

  window.addEventListener('resize', function () {
    if (ScrollTrigger) ScrollTrigger.refresh();
  });
})();
